import { get, isEmpty, reject } from 'lodash';

import {
  QUESTION_PROMPT_MAX_LENGTH,
  ANSWER_MAX_LENGTH,
} from 'containers/QuestionnairePage/constants';
import { hasLogicVisibilityError } from 'components/VisibilityActionButton/utils';
import {
  hasMissingQuotaFromItems,
  isZeroQuotaAllowed,
  hasWrongQuotaSumFromItemsMatrix,
  hasWrongMinimumQuotaSumFromItemsMatrix,
  hasQuotaLessThanCollectedFromItems,
  hasQuotaFilledFromItemsMatrix,
} from 'utils/quotaHelpers';
import { deriveQuotaMode, QUOTA_MODE } from 'data/quotaModes';
import { surveyStatusEnum } from 'containers/SurveysPage/constants';

import { isAnswerInvalid, isAnswerEmpty, isCarriedForward } from './utils';
import {
  MATRIX_MAX_COLUMNS,
  MATRIX_MAX_ROWS,
  MATRIX_MIN_COLUMNS,
  MATRIX_MIN_ROWS,
} from './constants';
import { FORWARD_ANSWER_AS } from '../../NodeSettings/settings/utils/constants';
import { getPromptFromHtml } from '../../../../components/FormattedInput/helpers';
import {
  hasIncompleteCarryForward,
  getAllCarriedForwardCount,
  onlyAnswerSelectionWithPossibleZeroCarryForward,
  matrixToMatrixUnselectedCarryForward,
} from '../../NodeSettings/settings/utils/CarryForward';

import { getPreviousNodeRefIdsForNode } from '../SingleMultipleQuestion/utils';

function validate({
  node,
  nodes,
  previousNodesPerNode = {},
  answersWithLogicFromOutcomes,
  screeningQuestions = [],
  allNodes = [],
}) {
  const {
    refId,
    answers = [],
    scorepoints = [],
    prompt,
    carryForward = [],
  } = node;
  const errors = { answerTooLong: {} };
  const carryForwardEnabled = Boolean(carryForward.length);

  if (isEmpty(prompt)) {
    errors.missingPrompt = true;
  }

  const questionTooLong = prompt && prompt.length > QUESTION_PROMPT_MAX_LENGTH;
  if (questionTooLong) {
    errors.questionTooLong = true;
  }

  const hasInvalidAnswers = answers.some(isAnswerInvalid);
  if (hasInvalidAnswers) {
    errors.emptyRows = true;
  }

  const hasInvalidScorepoints = scorepoints.some(isAnswerInvalid);

  if (hasInvalidScorepoints) {
    errors.emptyColumns = true;
  }

  // answer prompt max length check
  [answers, scorepoints].forEach((items) =>
    items.forEach((answer) => {
      const answerPrompt = getPromptFromHtml(answer.body);
      if (answerPrompt.length > ANSWER_MAX_LENGTH) {
        errors.answerTooLong[answer.refId] = true;
      }
    }),
  );

  const nonEmptyAnswers = reject(
    answers,
    (answer) => isAnswerEmpty(answer) || isCarriedForward(answer),
  );

  const nonEmptyScorepoints = reject(
    scorepoints,
    (scorepoint) => isAnswerEmpty(scorepoint) || isCarriedForward(scorepoint),
  );
  if (
    !carryForwardEnabled &&
    (nonEmptyAnswers.length < MATRIX_MIN_ROWS ||
      nonEmptyAnswers.length > MATRIX_MAX_ROWS)
  ) {
    errors.outOfLimitsRows = true;
  }

  if (
    !carryForwardEnabled &&
    (nonEmptyScorepoints.length < MATRIX_MIN_COLUMNS ||
      nonEmptyScorepoints.length > MATRIX_MAX_COLUMNS)
  ) {
    errors.outOfLimitsColumns = true;
  }

  if (carryForwardEnabled) {
    const carriedCounts = getAllCarriedForwardCount(carryForward, allNodes);
    const carriedAnswersCount = carriedCounts[FORWARD_ANSWER_AS.ANSWERS];
    const carriedScorepointsCount =
      carriedCounts[FORWARD_ANSWER_AS.SCOREPOINTS];

    if (carriedAnswersCount) {
      const hasEnoughCFanswers =
        carriedAnswersCount.min + nonEmptyAnswers.length >= MATRIX_MIN_ROWS;
      if (!hasEnoughCFanswers) {
        errors.missingAnswersWithCF = true;
      }
      if (nonEmptyAnswers.length + carriedAnswersCount.max > MATRIX_MAX_ROWS) {
        errors.tooManyAnswersWithCF = true;
      }
    }

    if (carriedScorepointsCount) {
      const hasEnoughCFscorepoints =
        carriedScorepointsCount.min + nonEmptyScorepoints.length >=
        MATRIX_MIN_COLUMNS;
      if (!hasEnoughCFscorepoints) {
        errors.missingScorepointsWithCF = true;
      }
      if (
        nonEmptyScorepoints.length + carriedScorepointsCount.max >
        MATRIX_MAX_COLUMNS
      ) {
        errors.tooManyScorepointsWithCF = true;
      }
    }

    const hasIncompleteCarryForwardConfigurations = hasIncompleteCarryForward(
      carryForward,
      allNodes,
    );
    if (hasIncompleteCarryForwardConfigurations) {
      errors.incompleteCarryForwardConfiguration = true;
    }

    const previousNodes = getPreviousNodeRefIdsForNode({
      refId,
      previousNodesPerNode,
      nodes,
      screeningNodes: screeningQuestions,
    });

    const hasUnusedCF = carryForward.some(
      ({ questionRefId }) => !previousNodes.includes(questionRefId),
    );
    if (hasUnusedCF) {
      errors.hasUnusedCF = true;
    }

    if (
      (answers.every(isCarriedForward) ||
        scorepoints.every(isCarriedForward)) &&
      (onlyAnswerSelectionWithPossibleZeroCarryForward(carryForward) ||
        matrixToMatrixUnselectedCarryForward({
          targetQuestion: node,
          allNodes,
        }))
    ) {
      if (errors.missingAnswersWithCF) {
        errors.onlyAnswerSelectionWithPossibleZeroCarryForwardRows = true;
        delete errors.missingAnswersWithCF;
      }
      if (errors.missingScorepointsWithCF) {
        errors.onlyAnswerSelectionWithPossibleZeroCarryForwardColumns = true;
        delete errors.missingScorepointsWithCF;
      }
    }
  }

  if (isEmpty(errors.answerTooLong)) {
    delete errors.answerTooLong;
  }

  if (
    hasLogicVisibilityError({
      nodeType: node.type,
      isScreening: node.isScreening,
      answers: nonEmptyAnswers,
      carriedAnswers: answers.filter(isCarriedForward),
      answersWithLogicFromOutcomes,
    })
  ) {
    errors.hasLogicVisibilityErrorOnRows = true;
  }

  if (
    hasLogicVisibilityError({
      nodeType: node.type,
      isScreening: node.isScreening,
      answers: nonEmptyScorepoints,
      carriedAnswers: scorepoints.filter(isCarriedForward),
      answersWithLogicFromOutcomes,
    })
  ) {
    errors.hasLogicVisibilityErrorOnColumns = true;
  }

  return errors;
}

export default validate;

export const validateQuotas = (
  node,
  nodeQuotas,
  uncoditionallyHiddenItems,
  surveyConfig,
  surveyStatusName,
) => {
  const { isDraft, completes } = surveyConfig;
  const errors = {};
  const isMinimumResponses =
    deriveQuotaMode({
      simpleQuotasPresent: true,
      quotaLimitType: node.quotaLimitType,
    }) === QUOTA_MODE.MIN;

  const isSurveyCompleted = surveyStatusName === surveyStatusEnum.COMPLETED;

  const answers = get(node, 'answers', []);
  const columns = get(node, 'scorepoints', []);

  const visibleAnswers = answers.filter(
    (answer) => !uncoditionallyHiddenItems.includes(answer.refId),
  );
  const visibleColumns = columns.filter(
    (scorepoint) => !uncoditionallyHiddenItems.includes(scorepoint.refId),
  );

  const visibleItemKeys = visibleAnswers.reduce((keys, answer) => {
    visibleColumns.forEach((column) => {
      keys.push(`${answer.refId}::${column.refId}`);
    });
    return keys;
  }, []);

  if (
    !isMinimumResponses &&
    !isSurveyCompleted &&
    hasQuotaFilledFromItemsMatrix(
      nodeQuotas.items,
      visibleAnswers,
      visibleColumns,
    )
  ) {
    errors.quotasFulfilled = true;
  }

  if (
    hasMissingQuotaFromItems(visibleItemKeys, nodeQuotas.items, {
      allowZeroQuota: isZeroQuotaAllowed({
        isDraft,
        isScreening: node.isScreening,
      }),
      isMinimumResponses,
    })
  ) {
    if (isMinimumResponses) {
      errors.minimumResponsesMissing = true;
    } else {
      errors.quotaMissing = true;
    }
    return errors;
  }

  if (!errors.quotasFulfilled && !isSurveyCompleted) {
    if (isMinimumResponses) {
      if (
        hasWrongMinimumQuotaSumFromItemsMatrix({
          nodeQuotaItems: nodeQuotas.items,
          remainingCompletes: completes,
          rows: visibleAnswers,
          columns: visibleColumns,
        })
      ) {
        errors.minimumQuotaSumExceedsCompletes = true;
        return errors;
      }
    } else if (
      hasWrongQuotaSumFromItemsMatrix({
        nodeQuotaItems: nodeQuotas.items,
        remainingCompletes: completes,
        rows: visibleAnswers,
        columns: visibleColumns,
      })
    ) {
      errors.quotaSumIsNot100 = true;
      return errors;
    }
  }

  if (
    !isMinimumResponses &&
    hasQuotaLessThanCollectedFromItems(visibleItemKeys, nodeQuotas.items)
  ) {
    errors.hasQuotaLessThanCollected = true;
    return errors;
  }

  return errors;
};
